"use client";
import { getApiBase } from "@/lib/apiBase";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import axios from "axios";

const API_BASE = getApiBase();

const toDateTimeLocalValue = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

export default function EditChapter({ chapterId, onCancel, onUpdated }) {
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("Active");
  const [publishAt, setPublishAt] = useState(toDateTimeLocalValue());
  const [seoTitle, setSeoTitle] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (chapterId) {
      axios
        .get(`${API_BASE}/chapters/${chapterId}`)
        .then((res) => {
          const chapter = res.data;
          setTitle(chapter.title || "");
          setStatus(chapter.status || "Active");
          setPublishAt(toDateTimeLocalValue(chapter.publishAt || chapter.updatedAt || chapter.createdAt));
          setSeoTitle(chapter.seoTitle || "");
          setSeoKeywords(chapter.seoKeywords || "");
          setSeoDescription(chapter.seoDescription || "");
          setMetaTitle(chapter.metaTitle || "");
          setMetaKeywords(chapter.metaKeywords || "");
          setMetaDescription(chapter.metaDescription || "");
        })
        .catch((error) => {
          Swal.fire("Error", "Failed to load chapter details", "error");
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [chapterId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      Swal.fire("Validation Error", "Chapter title is required.", "warning");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_BASE}/chapters/${chapterId}`, {
        title: title.trim(),
        status,
        publishAt: publishAt ? new Date(publishAt).toISOString() : undefined,
        seoTitle: seoTitle.trim(),
        seoKeywords: seoKeywords.trim(),
        seoDescription: seoDescription.trim(),
        metaTitle: metaTitle.trim(),
        metaKeywords: metaKeywords.trim(),
        metaDescription: metaDescription.trim(),
      });
      setLoading(false);
      Swal.fire("Success", "Chapter updated successfully!", "success");
      onUpdated(); // Notify parent to refresh and close the form
    } catch (error) {
      setLoading(false);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update chapter",
        "error"
      );
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading chapter details...</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        bgcolor: "white",
        p: 3,
        borderRadius: 2,
        boxShadow: 3,
        maxWidth: 800,
        mx: "auto",
      }}
    >
      <Typography variant="h6" mb={2}>
        Edit Chapter
      </Typography>

      <TextField
        label="Chapter Title"
        fullWidth
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          required
        >
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Date & Time"
        type="datetime-local"
        fullWidth
        value={publishAt}
        onChange={(e) => setPublishAt(e.target.value)}
        sx={{ mb: 3 }}
        InputLabelProps={{ shrink: true }}
        helperText="Managed chapter date and time"
      />

      {/* SEO Section */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">SEO Section</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="SEO Title"
              fullWidth
              value={seoTitle}
              onChange={(e) => setSeoTitle(e.target.value)}
              placeholder="Enter SEO title"
              helperText="Recommended: 50-60 characters"
            />
            
            <TextField
              label="SEO Keywords"
              fullWidth
              value={seoKeywords}
              onChange={(e) => setSeoKeywords(e.target.value)}
              placeholder="Enter keywords separated by commas"
              helperText="Separate keywords with commas"
            />
            
            <TextField
              label="SEO Description"
              fullWidth
              multiline
              rows={3}
              value={seoDescription}
              onChange={(e) => setSeoDescription(e.target.value)}
              placeholder="Enter SEO description"
              helperText="Recommended: 150-160 characters"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Meta Tags Section */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Meta Tags</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Meta Title"
              fullWidth
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="Enter meta title"
              helperText="Recommended: 50-60 characters"
            />
            
            <TextField
              label="Meta Keywords"
              fullWidth
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              placeholder="Enter keywords separated by commas"
              helperText="Separate keywords with commas"
            />
            
            <TextField
              label="Meta Description"
              fullWidth
              multiline
              rows={3}
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="Enter meta description"
              helperText="Recommended: 150-160 characters"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={loading}>
          {loading ? "Updating..." : "Update Chapter"}
        </Button>
      </Stack>
    </Box>
  );
}
